/**
 * Formatting actions for the Textarea's formatting bar.
 *
 * Free-text fields across the app are stored as plain strings holding a small
 * markdown subset — the one FormattedText renders and htmlToMarkdown produces
 * on paste: **bold**, "- " bullets and "1. " numbered items. These helpers
 * apply exactly those marks to a textarea's value and selection, so a field
 * gets formatting without changing what is stored or how it is displayed.
 *
 * Pure functions over (value, selection) → (value, selection), so the
 * textarea side is only "write the result back and restore the caret".
 */

export interface TextEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

const BOLD = "**";

/** Wraps the selection in **…**, or unwraps it when it is already bold. */
export const toggleBold = (value: string, start: number, end: number): TextEdit => {
  const selected = value.slice(start, end);

  // The selection itself includes the markers: "**word**".
  if (selected.length >= 4 && selected.startsWith(BOLD) && selected.endsWith(BOLD)) {
    const inner = selected.slice(2, -2);
    return {
      value: value.slice(0, start) + inner + value.slice(end),
      selectionStart: start,
      selectionEnd: start + inner.length,
    };
  }

  // The markers sit just outside the selection: **[word]**.
  if (value.slice(start - 2, start) === BOLD && value.slice(end, end + 2) === BOLD) {
    return {
      value: value.slice(0, start - 2) + selected + value.slice(end + 2),
      selectionStart: start - 2,
      selectionEnd: end - 2,
    };
  }

  // Nothing selected: drop in an empty pair and put the caret between them.
  if (start === end) {
    return {
      value: value.slice(0, start) + BOLD + BOLD + value.slice(end),
      selectionStart: start + 2,
      selectionEnd: start + 2,
    };
  }

  // Keep surrounding spaces outside the markers — "** word **" doesn't render
  // as bold, and double-clicking a word often selects its trailing space.
  const leading = selected.length - selected.trimStart().length;
  const trailing = selected.length - selected.trimEnd().length;
  const core = selected.trim();
  if (!core) return { value, selectionStart: start, selectionEnd: end };

  const innerStart = start + leading;
  const innerEnd = end - trailing;
  return {
    value: value.slice(0, innerStart) + BOLD + core + BOLD + value.slice(innerEnd),
    selectionStart: innerStart + 2,
    selectionEnd: innerEnd + 2,
  };
};

const BULLET_RE = /^[-*]\s+/;
const NUMBER_RE = /^\d+\.\s+/;

/**
 * Turns the lines the selection touches into a bulleted or numbered list, or
 * back into plain lines when they all already are one. Switching between the
 * two list kinds replaces the marker rather than stacking a second one.
 */
export const toggleList = (
  value: string,
  start: number,
  end: number,
  kind: "bullet" | "number",
): TextEdit => {
  // A selection that ends right after a newline doesn't really include the
  // next line, so don't format it.
  const effectiveEnd = end > start && value[end - 1] === "\n" ? end - 1 : end;
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const nextBreak = value.indexOf("\n", effectiveEnd);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;

  const lines = value.slice(lineStart, lineEnd).split("\n");
  const ownRe = kind === "bullet" ? BULLET_RE : NUMBER_RE;
  const contentLines = lines.filter((line) => line.trim());
  const alreadyList = contentLines.length > 0 && contentLines.every((line) => ownRe.test(line));

  let counter = 0;
  const nextLines = lines.map((line) => {
    if (alreadyList) return line.replace(ownRe, "");
    // In a multi-line selection blank lines stay blank; a single empty line
    // still gets a marker, so the user can start typing the first item.
    if (!line.trim() && lines.length > 1) return line;
    const text = line.replace(BULLET_RE, "").replace(NUMBER_RE, "");
    counter += 1;
    return `${kind === "bullet" ? "-" : `${counter}.`} ${text}`;
  });

  const block = nextLines.join("\n");
  const nextValue = value.slice(0, lineStart) + block + value.slice(lineEnd);

  // A caret on one line moves with its text; a selection keeps covering the
  // lines it covered.
  if (start === end && lines.length === 1) {
    const caret = Math.max(lineStart, start + (block.length - (lineEnd - lineStart)));
    return { value: nextValue, selectionStart: caret, selectionEnd: caret };
  }
  return { value: nextValue, selectionStart: lineStart, selectionEnd: lineStart + block.length };
};

/**
 * The same text with the markers removed — for short previews (cards,
 * truncated snippets) that show a bio as one plain line rather than rendering
 * it with FormattedText.
 */
export const stripMarkdown = (text: string): string =>
  text
    .split("\n")
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, "")
        .replace(BULLET_RE, "")
        .replace(NUMBER_RE, "")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/`([^`]+)`/g, "$1"),
    )
    .join("\n");
